import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Headphones, Mic, PenTool, TrendingUp, Calendar, CalendarCheck, Clock, Timer, Bell, X, Link, AlertTriangle, Settings, User, Mail, Lock, Phone, MapPin, Save, AlertCircle, XCircle, RefreshCw } from 'lucide-react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { settingsAPI, usersAPI } from '../services/api';
import { toast } from 'sonner';

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, updateUserInfo } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [effectiveSettings, setEffectiveSettings] = useState(null);
  
  // Account Settings Modal State
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [accountForm, setAccountForm] = useState({
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [savingAccount, setSavingAccount] = useState(false);

  // Load notifications
  useEffect(() => {
    const loadNotifications = async () => {
      try {
        // Get effective notifications for user (center-specific or global)
        const allNotifications = await settingsAPI.getEffectiveNotifications(user.id);
        setNotifications(allNotifications || []);
        
        // Count unread notifications for current user
        const unread = (allNotifications || []).filter(n => 
          !n.read_by && !n.readBy ? true : 
          !(n.read_by || n.readBy || []).includes(user?.id)
        ).length;
        setUnreadCount(unread);

        // Load effective settings for WhatsApp number
        const settings = await settingsAPI.getEffectiveSettings(user.id);
        const siteSettings = await settingsAPI.getSiteSettings();
        
        // Merge settings: use center settings if available, else site settings
        const finalSettings = {
          ...siteSettings,
          ...settings
        };
        
        console.log('Effective Settings:', finalSettings);
        setEffectiveSettings(finalSettings);
      } catch (error) {
        console.error('Error loading notifications/settings:', error);
      }
    };
    if (user?.id) {
      loadNotifications();
    }
  }, [user?.id]);

  // Initialize account form when modal opens
  useEffect(() => {
    if (showAccountSettings && user) {
      setAccountForm({
        email: user.email || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    }
  }, [showAccountSettings, user]);

  // Save account settings
  const handleSaveAccountSettings = async () => {
    // Validate new password if provided
    if (accountForm.newPassword) {
      if (!accountForm.currentPassword) {
        toast.error('Veuillez entrer votre mot de passe actuel');
        return;
      }
      if (accountForm.newPassword.length < 6) {
        toast.error('Le nouveau mot de passe doit contenir au moins 6 caractères');
        return;
      }
      if (accountForm.newPassword !== accountForm.confirmPassword) {
        toast.error('Les mots de passe ne correspondent pas');
        return;
      }
    }

    // Validate email
    if (accountForm.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(accountForm.email)) {
      toast.error('Email invalide');
      return;
    }

    setSavingAccount(true);

    try {
      // Update email if changed
      if (accountForm.email && accountForm.email !== user?.email) {
        await usersAPI.update(user?.id, { email: accountForm.email });
      }
      
      // Update password if provided
      if (accountForm.newPassword) {
        await usersAPI.updatePassword(user?.id, accountForm.currentPassword, accountForm.newPassword);
      }

      toast.success('Paramètres mis à jour avec succès');
      setShowAccountSettings(false);
      setAccountForm({
        email: '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      // Update local user state
      if (accountForm.email && accountForm.email !== user?.email && updateUserInfo) {
        updateUserInfo(user?.id, { email: accountForm.email });
      }
    } catch (error) {
      console.error('Error saving account settings:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSavingAccount(false);
    }
  };

  // Mark notification as read
  const markAsRead = async (notifId) => {
    try {
      await settingsAPI.markEffectiveNotificationRead(notifId, user?.id);
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Open notification modal
  const openNotification = (notif) => {
    setSelectedNotification(notif);
    setShowNotificationModal(true);
    markAsRead(notif.id);
  };

  // Calculate subscription info
  const getSubscriptionInfo = () => {
    const startDate = user?.debut_abonnement ? new Date(user.debut_abonnement) : null;
    const endDate = user?.fin_abonnement ? new Date(user.fin_abonnement) : null;
    const now = new Date();
    
    let totalDays = 0;
    let remainingDays = 0;
    let isActive = false;
    
    if (startDate && endDate) {
      totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
      remainingDays = Math.max(0, Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)));
      isActive = endDate > now;
    }
    
    return {
      startDate,
      endDate,
      totalDays,
      remainingDays,
      isActive,
      packName: user?.pack || user?.type_pack || null
    };
  };

  const subscriptionInfo = getSubscriptionInfo();

  // Subscription expiration alert logic
  const getExpirationAlert = () => {
    const { remainingDays, isActive, endDate, packName } = subscriptionInfo;
    
    // No subscription date = no alert
    if (!endDate) return null;
    
    // Already expired
    if (!isActive || remainingDays <= 0) {
      return {
        type: 'expired',
        severity: 'critical',
        bgColor: 'bg-red-500',
        borderColor: 'border-red-600',
        textColor: 'text-white',
        icon: XCircle,
        title: 'Abonnement expiré',
        message: 'Votre abonnement a expiré. Renouvelez maintenant pour continuer à accéder à tous les contenus.',
        showRenew: true
      };
    }
    
    // Expires in less than 3 days - Critical
    if (remainingDays <= 3) {
      return {
        type: 'critical',
        severity: 'critical',
        bgColor: 'bg-red-500',
        borderColor: 'border-red-600',
        textColor: 'text-white',
        icon: AlertCircle,
        title: remainingDays === 1 ? 'Dernier jour !' : `Plus que ${remainingDays} jours`,
        message: `Votre abonnement expire ${remainingDays === 1 ? 'demain' : `dans ${remainingDays} jours`}. Renouvelez maintenant pour éviter toute interruption.`,
        showRenew: true
      };
    }
    
    // Expires in 4-7 days - Warning
    if (remainingDays <= 7) {
      return {
        type: 'warning',
        severity: 'warning',
        bgColor: 'bg-orange-500',
        borderColor: 'border-orange-600',
        textColor: 'text-white',
        icon: AlertTriangle,
        title: `Expiration proche`,
        message: `Votre abonnement expire dans ${remainingDays} jours. Pensez à le renouveler pour ne pas perdre l'accès.`,
        showRenew: true
      };
    }
    
    // Expires in 8-14 days - Info
    if (remainingDays <= 14) {
      return {
        type: 'info',
        severity: 'info',
        bgColor: 'bg-yellow-500',
        borderColor: 'border-yellow-600',
        textColor: 'text-yellow-900',
        icon: Clock,
        title: `Rappel d'abonnement`,
        message: `Votre abonnement expire dans ${remainingDays} jours. N'oubliez pas de le renouveler.`,
        showRenew: true
      };
    }
    
    return null;
  };

  const expirationAlert = getExpirationAlert();
  const [dismissedAlert, setDismissedAlert] = useState(false);

  const formatDate = (date) => {
    if (!date) return '-';
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const modules = [
    {
      id: 'comprehension-ecrite',
      title: 'Compréhension Écrite',
      description: 'Améliorez votre lecture et compréhension de textes',
      icon: BookOpen,
      color: 'bg-blue-500',
      path: '/modules/comprehension-ecrite'
    },
    {
      id: 'comprehension-orale',
      title: 'Compréhension Orale',
      description: 'Développez votre écoute et compréhension audio',
      icon: Headphones,
      color: 'bg-green-500',
      path: '/modules/comprehension-orale'
    },
    {
      id: 'expression-ecrite',
      title: 'Expression Écrite',
      description: 'Perfectionnez votre rédaction et style',
      icon: PenTool,
      color: 'bg-purple-500',
      path: '/modules/expression-ecrite'
    },
    {
      id: 'expression-orale',
      title: 'Expression Orale',
      description: 'Pratiquez votre prononciation et fluidité',
      icon: Mic,
      color: 'bg-orange-500',
      path: '/modules/expression-orale'
    }
  ];

  return (
    <Layout>
      <div className="space-y-8 animate-in">
        {/* Subscription Expiration Alert Banner */}
        {expirationAlert && !dismissedAlert && (
          <div 
            className={`${expirationAlert.bgColor} ${expirationAlert.textColor} rounded-xl p-4 shadow-lg animate-in border-2 ${expirationAlert.borderColor}`}
            data-testid="subscription-alert"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start space-x-3">
                <div className={`p-2 rounded-full ${expirationAlert.severity === 'critical' ? 'bg-white/20' : 'bg-black/10'}`}>
                  <expirationAlert.icon className="h-6 w-6" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-lg">{expirationAlert.title}</h3>
                  <p className={`text-sm mt-1 ${expirationAlert.severity === 'info' ? 'text-yellow-800' : 'text-white/90'}`}>
                    {expirationAlert.message}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 flex-shrink-0">
                {expirationAlert.showRenew && (
                  <button
                    onClick={() => navigate('/tarification')}
                    data-testid="renew-subscription-btn"
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-semibold transition-all ${
                      expirationAlert.severity === 'info' 
                        ? 'bg-yellow-800 text-white hover:bg-yellow-900' 
                        : 'bg-white text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <RefreshCw className="h-4 w-4" />
                    <span>Renouveler</span>
                  </button>
                )}
                <button
                  onClick={() => setDismissedAlert(true)}
                  className={`p-2 rounded-lg transition-colors ${
                    expirationAlert.severity === 'info' 
                      ? 'hover:bg-yellow-600/30' 
                      : 'hover:bg-white/20'
                  }`}
                  data-testid="dismiss-alert-btn"
                  title="Fermer l'alerte"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="space-y-2">
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold tracking-tight">Tableau de bord</h1>
          <p className="text-base text-muted-foreground">
            Bienvenue {user?.username}! Sélectionnez un module pour commencer votre préparation.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Notification Box */}
          <div 
            className="bg-card p-6 rounded-xl border shadow-sm cursor-pointer hover:shadow-md transition-all relative overflow-hidden group" 
            data-testid="notifications-card"
            onClick={() => {
              if (notifications.length > 0) {
                openNotification(notifications[0]);
              }
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-muted-foreground">Notifications</p>
                {notifications.length > 0 ? (
                  <div className="mt-2">
                    <p className="text-lg font-semibold line-clamp-1">{notifications[0]?.title}</p>
                    <p className="text-sm text-muted-foreground line-clamp-1">{notifications[0]?.message}</p>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground mt-2">Aucune notification</p>
                )}
              </div>
              {/* Gyrophare / Siren Icon */}
              <div className="relative">
                {unreadCount > 0 ? (
                  <div className="relative">
                    {/* Animated siren effect */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="absolute h-14 w-14 rounded-full bg-red-500/20 animate-ping" />
                      <div className="absolute h-12 w-12 rounded-full bg-orange-500/30 animate-pulse" />
                    </div>
                    {/* Spinning siren icon */}
                    <div className="relative z-10 animate-spin-slow">
                      <AlertTriangle className="h-12 w-12 text-red-500 drop-shadow-lg" />
                    </div>
                    {/* Unread badge */}
                    <div className="absolute -top-1 -right-1 h-6 w-6 bg-red-600 rounded-full flex items-center justify-center text-white text-xs font-bold z-20 animate-bounce">
                      {unreadCount}
                    </div>
                  </div>
                ) : (
                  <Bell className="h-12 w-12 text-primary opacity-20" />
                )}
              </div>
            </div>
            {notifications.length > 1 && (
              <p className="text-xs text-muted-foreground mt-3 border-t pt-2">
                + {notifications.length - 1} autre(s) notification(s)
              </p>
            )}
          </div>

          <div 
            className="bg-card p-6 rounded-xl border shadow-sm cursor-pointer hover:shadow-md transition-all group"
            onClick={() => setShowAccountSettings(true)}
            data-testid="account-settings-card"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Paramètres du compte</p>
                <p className="text-lg font-semibold text-primary mt-2 group-hover:underline">Modifier mes informations</p>
              </div>
              <Settings className="h-12 w-12 text-primary opacity-20 group-hover:opacity-40 transition-opacity" />
            </div>
          </div>

          <div className="bg-card p-6 rounded-xl border shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Statut du compte</p>
                <div className="flex items-center space-x-2 mt-1">
                  <p className={`text-2xl font-bold ${subscriptionInfo.isActive ? 'text-green-500' : 'text-gray-500'}`}>
                    {subscriptionInfo.isActive ? 'Actif' : 'Inactif'}
                  </p>
                  {subscriptionInfo.packName && (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium text-white ${
                      subscriptionInfo.packName === 'gold' ? 'bg-yellow-500' :
                      subscriptionInfo.packName === 'silver' ? 'bg-gray-400' :
                      'bg-amber-600'
                    }`}>
                      {subscriptionInfo.packName.charAt(0).toUpperCase() + subscriptionInfo.packName.slice(1)}
                    </span>
                  )}
                </div>
              </div>
              <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                subscriptionInfo.isActive ? 'bg-green-500/20' : 'bg-gray-500/20'
              }`}>
                <div className={`h-5 w-5 rounded-full ${subscriptionInfo.isActive ? 'bg-green-500' : 'bg-gray-500'}`} />
              </div>
            </div>
            
            {/* Subscription Details Grid */}
            <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border">
              {/* Start Date */}
              <div className="flex items-center space-x-2">
                <div className="p-1.5 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                  <Calendar className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Début</p>
                  <p className="text-sm font-semibold">{formatDate(subscriptionInfo.startDate)}</p>
                </div>
              </div>
              
              {/* End Date */}
              <div className="flex items-center space-x-2">
                <div className="p-1.5 rounded-lg bg-red-100 dark:bg-red-900/30">
                  <CalendarCheck className="h-4 w-4 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Fin</p>
                  <p className="text-sm font-semibold">{formatDate(subscriptionInfo.endDate)}</p>
                </div>
              </div>
              
              {/* Total Duration */}
              <div className="flex items-center space-x-2">
                <div className="p-1.5 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                  <Clock className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Durée</p>
                  <p className="text-sm font-semibold">{subscriptionInfo.totalDays} jours</p>
                </div>
              </div>
              
              {/* Remaining Days */}
              <div className="flex items-center space-x-2">
                <div className={`p-1.5 rounded-lg ${
                  subscriptionInfo.remainingDays > 7 
                    ? 'bg-green-100 dark:bg-green-900/30' 
                    : subscriptionInfo.remainingDays > 0 
                    ? 'bg-orange-100 dark:bg-orange-900/30' 
                    : 'bg-gray-100 dark:bg-gray-900/30'
                }`}>
                  <Timer className={`h-4 w-4 ${
                    subscriptionInfo.remainingDays > 7 
                      ? 'text-green-600 dark:text-green-400' 
                      : subscriptionInfo.remainingDays > 0 
                      ? 'text-orange-600 dark:text-orange-400' 
                      : 'text-gray-600 dark:text-gray-400'
                  }`} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Restant</p>
                  <p className={`text-sm font-semibold ${
                    subscriptionInfo.remainingDays > 7 
                      ? 'text-green-600 dark:text-green-400' 
                      : subscriptionInfo.remainingDays > 0 
                      ? 'text-orange-600 dark:text-orange-400' 
                      : 'text-gray-500'
                  }`}>
                    {subscriptionInfo.remainingDays} jours
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modules Grid */}
        <div>
          <h2 className="text-2xl font-semibold mb-6">Modules de formation</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {modules.map((module) => {
              const Icon = module.icon;
              return (
                <button
                  key={module.id}
                  onClick={() => navigate(module.path)}
                  data-testid={`module-card-${module.id}`}
                  className="bg-card p-8 rounded-xl border shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 text-left group"
                >
                  <div className="flex items-start space-x-4">
                    <div className={`${module.color} p-4 rounded-xl text-white`}>
                      <Icon className="h-8 w-8" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                        {module.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {module.description}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Notification Modal */}
      {showNotificationModal && selectedNotification && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="notification-modal">
          <div className="bg-card rounded-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto shadow-2xl animate-in">
            <div className="p-4 border-b flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Bell className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">Notification</h3>
              </div>
              <button
                onClick={() => {
                  setShowNotificationModal(false);
                  setSelectedNotification(null);
                }}
                className="p-2 rounded-lg hover:bg-secondary transition-colors"
                data-testid="close-notification-modal"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <h2 className="text-xl font-bold">{selectedNotification.title}</h2>
              
              {/* WhatsApp Icons Row */}
              {effectiveSettings?.whatsapp && (
                <div className="flex items-center justify-between py-2 border-y border-dashed border-primary/20">
                  <a
                    href={`https://wa.me/${effectiveSettings.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(`Bonjour, j'ai une question concernant la notification : ${selectedNotification.title}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-full bg-green-500 text-white hover:bg-green-600 transition-all hover:scale-110 shadow-md"
                    title="Contacter sur WhatsApp"
                  >
                    <Phone className="h-5 w-5" />
                  </a>
                  <span className="text-xs font-medium text-green-600 dark:text-green-400">Besoin d'aide ? Contactez l'admin</span>
                  <a
                    href={`https://wa.me/${effectiveSettings.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(`Bonjour, j'ai une question concernant la notification : ${selectedNotification.title}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-full bg-green-500 text-white hover:bg-green-600 transition-all hover:scale-110 shadow-md"
                    title="Contacter sur WhatsApp"
                  >
                    <Phone className="h-5 w-5" />
                  </a>
                </div>
              )}

              <p className="text-muted-foreground whitespace-pre-wrap">{selectedNotification.message}</p>
              
              {selectedNotification.type === 'image' && (selectedNotification.media_url || selectedNotification.mediaUrl) && (
                <img src={selectedNotification.media_url || selectedNotification.mediaUrl} alt="" className="w-full rounded-lg" />
              )}
              
              {selectedNotification.type === 'video' && (selectedNotification.media_url || selectedNotification.mediaUrl) && (
                <video src={selectedNotification.media_url || selectedNotification.mediaUrl} controls className="w-full rounded-lg" />
              )}

              {selectedNotification.type === 'audio' && (selectedNotification.media_url || selectedNotification.mediaUrl) && (
                <div className="bg-secondary/50 rounded-xl p-4">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="h-10 w-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-orange-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/></svg>
                    </div>
                    <span className="font-medium">Message vocal</span>
                  </div>
                  <audio src={selectedNotification.media_url || selectedNotification.mediaUrl} controls className="w-full" />
                </div>
              )}
              
              {selectedNotification.type === 'link' && (selectedNotification.link_url || selectedNotification.linkUrl) && (
                <a
                  href={(() => {
                    const url = selectedNotification.link_url || selectedNotification.linkUrl;
                    if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
                      return 'https://' + url;
                    }
                    return url;
                  })()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 px-6 py-3 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  <Link className="h-4 w-4" />
                  <span>{selectedNotification.link_text || selectedNotification.linkText || 'Ouvrir le lien'}</span>
                </a>
              )}
              
              <p className="text-xs text-muted-foreground pt-4 border-t">
                Publié le {new Date(selectedNotification.created_at || selectedNotification.createdAt).toLocaleString('fr-FR')}
              </p>
            </div>

            {/* Other notifications list */}
            {notifications.length > 1 && (
              <div className="border-t">
                <div className="p-4">
                  <h4 className="text-sm font-medium text-muted-foreground mb-3">Autres notifications</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {notifications.filter(n => n.id !== selectedNotification.id).map((notif) => (
                      <div key={notif.id} className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setSelectedNotification(notif);
                            markAsRead(notif.id);
                          }}
                          className={`flex-1 text-left p-3 rounded-lg hover:bg-secondary transition-colors ${
                            !(notif.read_by || notif.readBy || []).includes(user?.id) ? 'bg-primary/5 border-l-4 border-primary' : ''
                          }`}
                        >
                          <p className="font-medium text-sm line-clamp-1">{notif.title}</p>
                          <p className="text-xs text-muted-foreground">{new Date(notif.created_at || notif.createdAt).toLocaleDateString('fr-FR')}</p>
                        </button>
                        {effectiveSettings?.whatsapp && (
                          <a
                            href={`https://wa.me/${effectiveSettings.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(`Bonjour, j'ai une question concernant la notification : ${notif.title}`)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 rounded-lg bg-green-500/10 text-green-600 hover:bg-green-500 hover:text-white transition-all shadow-sm"
                            title="Contacter sur WhatsApp"
                          >
                            <Phone className="h-4 w-4" />
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Account Settings Modal */}
      {showAccountSettings && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="account-settings-modal">
          <div className="bg-card rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-in">
            <div className="p-4 border-b flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Settings className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">Paramètres du compte</h3>
              </div>
              <button
                onClick={() => setShowAccountSettings(false)}
                className="p-2 rounded-lg hover:bg-secondary transition-colors"
                data-testid="close-account-settings"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* User Info Display */}
              <div className="bg-secondary/50 rounded-xl p-4 space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center">
                    <User className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold">{user?.nom || user?.username}</p>
                    <p className="text-sm text-muted-foreground">@{user?.username}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center space-x-2 text-muted-foreground">
                    <Phone className="h-4 w-4" />
                    <span>{user?.telephone || 'Non renseigné'}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-muted-foreground">
                    <MapPin className="h-4 w-4" />
                    <span>{user?.pays || 'Non renseigné'}</span>
                  </div>
                </div>
              </div>

              {/* Change Email */}
              <div className="space-y-2">
                <label className="flex items-center space-x-2 text-sm font-medium">
                  <Mail className="h-4 w-4" />
                  <span>Adresse email</span>
                </label>
                <input
                  type="email"
                  value={accountForm.email}
                  onChange={(e) => setAccountForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-2 rounded-lg border bg-background"
                  placeholder="votre@email.com"
                  data-testid="account-email-input"
                />
              </div>

              {/* Change Password Section */}
              <div className="space-y-4 pt-4 border-t">
                <h4 className="font-medium flex items-center space-x-2">
                  <Lock className="h-4 w-4" />
                  <span>Changer le mot de passe</span>
                </h4>
                
                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground">Mot de passe actuel</label>
                  <input
                    type="password"
                    value={accountForm.currentPassword}
                    onChange={(e) => setAccountForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                    className="w-full px-4 py-2 rounded-lg border bg-background"
                    placeholder="••••••••"
                    data-testid="current-password-input"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground">Nouveau mot de passe</label>
                  <input
                    type="password"
                    value={accountForm.newPassword}
                    onChange={(e) => setAccountForm(prev => ({ ...prev, newPassword: e.target.value }))}
                    className="w-full px-4 py-2 rounded-lg border bg-background"
                    placeholder="••••••••"
                    data-testid="new-password-input"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground">Confirmer le nouveau mot de passe</label>
                  <input
                    type="password"
                    value={accountForm.confirmPassword}
                    onChange={(e) => setAccountForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    className="w-full px-4 py-2 rounded-lg border bg-background"
                    placeholder="••••••••"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              {/* Save Button */}
              <div className="flex flex-wrap gap-3 justify-end pt-4">
                <button
                  onClick={() => setShowAccountSettings(false)}
                  className="px-4 py-2 rounded-lg border hover:bg-secondary transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSaveAccountSettings}
                  disabled={savingAccount}
                  className="flex items-center space-x-2 px-6 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
                  data-testid="save-account-settings"
                >
                  <Save className="h-4 w-4" />
                  <span>{savingAccount ? 'Enregistrement...' : 'Enregistrer'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}